/// <amd-module name="Pipelines/Client/RestClient/Pipelines" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   tfs\client\pipelines\azure\webapi\clientgeneratorconfigs\pipelines.genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import * as Pipelines from "Pipelines/Client/Contracts/Pipelines";

export interface IPipelinesRestClient extends IVssRestClient {
    /**
     * Get a specific log from a pipeline run
     * 
     * @param project - Project ID or project name
     * @param pipelineId - ID of the pipeline.
     * @param runId - ID of the run of that pipeline.
     * @param logId - ID of the log.
     * @param expand - Expand options. Default is None.
     */
    getLog(project: string, pipelineId: number, runId: number, logId: number, expand?: Pipelines.GetLogExpandOptions): Promise<Pipelines.Log>;
    /**
     * Get a list of logs from a pipeline run.
     * 
     * @param project - Project ID or project name
     * @param pipelineId - ID of the pipeline.
     * @param runId - ID of the run of that pipeline.
     * @param expand - Expand options. Default is None.
     */
    listLogs(project: string, pipelineId: number, runId: number, expand?: Pipelines.GetLogExpandOptions): Promise<Pipelines.LogCollection>;
    /**
     * Create a pipeline.
     * 
     * @param inputParameters - Input parameters.
     * @param project - Project ID or project name
     */
    createPipeline(inputParameters: Pipelines.CreatePipelineParameters, project: string): Promise<Pipelines.Pipeline>;
    /**
     * Gets a pipeline, optionally at the specified version
     * 
     * @param project - Project ID or project name
     * @param pipelineId - The pipeline ID
     * @param pipelineVersion - The pipeline version
     */
    getPipeline(project: string, pipelineId: number, pipelineVersion?: number): Promise<Pipelines.Pipeline>;
    /**
     * Get a list of pipelines.
     * 
     * @param project - Project ID or project name
     * @param orderBy - A sort expression. Defaults to "name asc"
     * @param top - The maximum number of pipelines to return
     * @param continuationToken - A continuation token from a previous request, to retrieve the next page of results
     */
    listPipelines(project: string, orderBy?: string, top?: number, continuationToken?: string): Promise<Pipelines.Pipeline[]>;
    /**
     * Queues a dry run of the pipeline and returns an object containing the final yaml.
     * 
     * @param runParameters - Optional additional parameters for this run.
     * @param project - Project ID or project name
     * @param pipelineId - The pipeline ID.
     * @param pipelineVersion - The pipeline version.
     */
    preview(runParameters: Pipelines.RunPipelineParameters, project: string, pipelineId: number, pipelineVersion?: number): Promise<Pipelines.PreviewRun>;
    /**
     * Gets a run for a particular pipeline.
     * 
     * @param project - Project ID or project name
     * @param pipelineId - The pipeline id
     * @param runId - The run id
     */
    getRun(project: string, pipelineId: number, runId: number): Promise<Pipelines.Run>;
    /**
     * Gets top 10000 runs for a particular pipeline.
     * 
     * @param project - Project ID or project name
     * @param pipelineId - The pipeline id
     */
    listRuns(project: string, pipelineId: number): Promise<Pipelines.Run[]>;
    /**
     * Runs a pipeline.
     * 
     * @param runParameters - Optional additional parameters for this run.
     * @param project - Project ID or project name
     * @param pipelineId - The pipeline ID.
     * @param pipelineVersion - The pipeline version.
     */
    runPipeline(runParameters: Pipelines.RunPipelineParameters, project: string, pipelineId: number, pipelineVersion?: number): Promise<Pipelines.Run>;
}

class PipelinesRestClient extends RestClientBase implements IPipelinesRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Get a specific log from a pipeline run
     * 
     * @param project - Project ID or project name
     * @param pipelineId - ID of the pipeline.
     * @param runId - ID of the run of that pipeline.
     * @param logId - ID of the log.
     * @param expand - Expand options. Default is None.
     */
    public async getLog(
        project: string,
        pipelineId: number,
        runId: number,
        logId: number,
        expand?: Pipelines.GetLogExpandOptions
        ): Promise<Pipelines.Log> {

        const queryValues: any = {
            '$expand': expand
        };

        return this.beginRequest<Pipelines.Log>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/pipelines/{pipelineId}/runs/{runId}/logs/{logId}",
            routeValues: {
                project: project,
                pipelineId: pipelineId,
                runId: runId,
                logId: logId
            },
            queryParams: queryValues
        });
    }

    /**
     * Get a list of logs from a pipeline run.
     * 
     * @param project - Project ID or project name
     * @param pipelineId - ID of the pipeline.
     * @param runId - ID of the run of that pipeline.
     * @param expand - Expand options. Default is None.
     */
    public async listLogs(
        project: string,
        pipelineId: number,
        runId: number,
        expand?: Pipelines.GetLogExpandOptions
        ): Promise<Pipelines.LogCollection> {

        const queryValues: any = {
            '$expand': expand
        };

        return this.beginRequest<Pipelines.LogCollection>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/pipelines/{pipelineId}/runs/{runId}/logs/{logId}",
            routeValues: {
                project: project,
                pipelineId: pipelineId,
                runId: runId
            },
            queryParams: queryValues
        });
    }

    /**
     * Create a pipeline.
     * 
     * @param inputParameters - Input parameters.
     * @param project - Project ID or project name
     */
    public async createPipeline(
        inputParameters: Pipelines.CreatePipelineParameters,
        project: string
        ): Promise<Pipelines.Pipeline> {

        return this.beginRequest<Pipelines.Pipeline>({
            apiVersion: "5.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/pipelines/{pipelineId}",
            routeValues: {
                project: project
            },
            body: inputParameters
        });
    }

    /**
     * Gets a pipeline, optionally at the specified version
     * 
     * @param project - Project ID or project name
     * @param pipelineId - The pipeline ID
     * @param pipelineVersion - The pipeline version
     */
    public async getPipeline(
        project: string,
        pipelineId: number,
        pipelineVersion?: number
        ): Promise<Pipelines.Pipeline> {

        const queryValues: any = {
            pipelineVersion: pipelineVersion
        };

        return this.beginRequest<Pipelines.Pipeline>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/pipelines/{pipelineId}",
            routeValues: {
                project: project,
                pipelineId: pipelineId
            },
            queryParams: queryValues
        });
    }

    /**
     * Get a list of pipelines.
     * 
     * @param project - Project ID or project name
     * @param orderBy - A sort expression. Defaults to "name asc"
     * @param top - The maximum number of pipelines to return
     * @param continuationToken - A continuation token from a previous request, to retrieve the next page of results
     */
    public async listPipelines(
        project: string,
        orderBy?: string,
        top?: number,
        continuationToken?: string
        ): Promise<Pipelines.Pipeline[]> {

        const queryValues: any = {
            orderBy: orderBy,
            '$top': top,
            continuationToken: continuationToken
        };

        return this.beginRequest<Pipelines.Pipeline[]>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/pipelines/{pipelineId}",
            routeValues: {
                project: project
            },
            queryParams: queryValues
        });
    }

    /**
     * Queues a dry run of the pipeline and returns an object containing the final yaml.
     * 
     * @param runParameters - Optional additional parameters for this run.
     * @param project - Project ID or project name
     * @param pipelineId - The pipeline ID.
     * @param pipelineVersion - The pipeline version.
     */
    public async preview(
        runParameters: Pipelines.RunPipelineParameters,
        project: string,
        pipelineId: number,
        pipelineVersion?: number
        ): Promise<Pipelines.PreviewRun> {

        const queryValues: any = {
            pipelineVersion: pipelineVersion
        };

        return this.beginRequest<Pipelines.PreviewRun>({
            apiVersion: "5.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/pipelines/{pipelineId}/preview",
            routeValues: {
                project: project,
                pipelineId: pipelineId
            },
            queryParams: queryValues,
            body: runParameters
        });
    }

    /**
     * Gets a run for a particular pipeline.
     * 
     * @param project - Project ID or project name
     * @param pipelineId - The pipeline id
     * @param runId - The run id
     */
    public async getRun(
        project: string,
        pipelineId: number,
        runId: number
        ): Promise<Pipelines.Run> {

        return this.beginRequest<Pipelines.Run>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/pipelines/{pipelineId}/runs/{runId}",
            routeValues: {
                project: project,
                pipelineId: pipelineId,
                runId: runId
            }
        });
    }

    /**
     * Gets top 10000 runs for a particular pipeline.
     * 
     * @param project - Project ID or project name
     * @param pipelineId - The pipeline id
     */
    public async listRuns(
        project: string,
        pipelineId: number
        ): Promise<Pipelines.Run[]> {

        return this.beginRequest<Pipelines.Run[]>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/pipelines/{pipelineId}/runs/{runId}",
            routeValues: {
                project: project,
                pipelineId: pipelineId
            }
        });
    }

    /**
     * Runs a pipeline.
     * 
     * @param runParameters - Optional additional parameters for this run.
     * @param project - Project ID or project name
     * @param pipelineId - The pipeline ID.
     * @param pipelineVersion - The pipeline version.
     */
    public async runPipeline(
        runParameters: Pipelines.RunPipelineParameters,
        project: string,
        pipelineId: number,
        pipelineVersion?: number
        ): Promise<Pipelines.Run> {

        const queryValues: any = {
            pipelineVersion: pipelineVersion
        };

        return this.beginRequest<Pipelines.Run>({
            apiVersion: "5.1-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/pipelines/{pipelineId}/runs/{runId}",
            routeValues: {
                project: project,
                pipelineId: pipelineId
            },
            queryParams: queryValues,
            body: runParameters
        });
    }

}

export const PipelinesClientName = "IPipelinesRestClient";

export function getPipelinesClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IPipelinesRestClient {
    return pageContext.getRestClient<IPipelinesRestClient>(PipelinesClientName, options);
}

RestClients.add(PipelinesClientName, { 
    factory: PipelinesRestClient,
    options: {
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});
